import { FlatfileRecord } from '@flatfile/hooks';
import { DATETIME_DESCRIPTION, FIELD_TYPE, submitAction } from 'components/imports/blueprintUtils';
import { nullable, required } from 'components/imports/schemaUtils';
import { ImportStrategy } from 'components/imports/types';
import { CONTACT_ACTIVITIES, requiresSubject } from 'constants/contacts';
import { IMPORT_CONTACT_ACTIVITIES_LINK } from 'constants/links';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useTimezones } from 'customHooks/useTimezones';
import { yup } from 'helpers/yup';
import { ValidationError } from 'yup';

const contactActivitiesSchema = yup.object().shape({
    contact_id: yup.number().label('Contact ID').required().min(0),
    type: required.label('Activity type').oneOf(
        CONTACT_ACTIVITIES.map(activity => activity.value),
        'Activity type must be one of the listed options.',
    ),
    subject: nullable
        .label('Subject')
        .when('$requiresSubject', ([required], schema) =>
            required
                ? schema.required('${label} is required for phone call and email types.')
                : schema.nullable(),
        ),
    note: nullable.label('Note'),
    occurred_at: required.label('Date occurred').date({ before: true }),
});

const useContactActivityImportStrategy = (): ImportStrategy => {
    const timezones = useTimezones();
    const { timezone: accountTz } = useCurrentAccount();

    const schema = contactActivitiesSchema.concat(
        yup.object().shape({
            timezone: nullable.label('Time zone').oneOf(
                timezones.map(tz => tz.name),
                'Must provide a valid time zone',
            ),
        }),
    );

    return {
        displayName: 'contact activities',
        type: 'contact_activities',
        hasDownloadTemplate: false,
        helpCenterUrl: IMPORT_CONTACT_ACTIVITIES_LINK,
        blueprint: {
            name: 'Import contact activities',
            sheets: [
                {
                    name: 'Contact activities',
                    description: 'Migrate contact activities from another platform.',
                    slug: 'contact_activities',
                    readonly: false,
                    allowAdditionalFields: true,
                    fields: [
                        {
                            label: 'Contact ID',
                            key: 'contact_id',
                            type: FIELD_TYPE.number,
                        },
                        {
                            label: 'Activity type',
                            key: 'type',
                            type: FIELD_TYPE.enum,
                            config: {
                                options: CONTACT_ACTIVITIES,
                            },
                        },
                        {
                            label: 'Subject',
                            key: 'subject',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Note',
                            key: 'note',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Date occurred',
                            key: 'occurred_at',
                            type: FIELD_TYPE.string,
                            description: DATETIME_DESCRIPTION,
                        },
                        {
                            label: 'Time zone',
                            key: 'timezone',
                            type: FIELD_TYPE.string,
                        },
                    ],
                },
            ],
            actions: [submitAction],
        },
        recordHook: (record: FlatfileRecord) => {
            const data = record.value;

            const context = {
                requiresSubject: data.type && requiresSubject.has(String(data.type)),
            };

            try {
                schema.validateSync(data, { context, abortEarly: false });
            } catch (err) {
                if (!(err instanceof ValidationError)) {
                    return record;
                }

                if (!err.inner) {
                    record.addError(err.path!, err.message);

                    return record;
                }

                err.inner.forEach((e: any) => {
                    record.addError(e.path, e.message);
                });
            }

            if (data.timezone === null || data.timezone === '') {
                record.addWarning(
                    'timezone',
                    `Time zone not provided — will default to account time zone: ${accountTz}`,
                );
            }

            return record;
        },
    };
};

export { useContactActivityImportStrategy };
